
import { Component, OnInit  } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, NavigationEnd } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { AuthService } from './auth/auth.service';
import { ClientStoreService } from './services/client-store.service';
import { ChargebeeService } from './services/chargebee/chargebee.service';
import { LocalStorageService } from './services/local-storage.service';
import { CustomTranslateService } from './services/custom-translate.service';
import { RecordService } from './services/record.service';

import { environment } from '../environments/environment';
import { MessagingService } from './services/messaging.service';
import { blank, fallback, filled, OptionalValue, spf } from './shared/utils/common';
import { get, trim } from 'lodash-es';
import { AVAILABLE_LANGUAGE_CODES } from './shared/utils/constants';
import { filter } from 'rxjs/operators';
import { SplashScreenService } from './services/splash-screen.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [
    TranslateService,
    CustomTranslateService
  ],
})
export class AppComponent implements OnInit {

  constructor(
    public auth: AuthService,
    public translate: TranslateService,
    public clientStoreService: ClientStoreService,
    public chargebeeService: ChargebeeService,
    protected localStorageService: LocalStorageService,
    private customTranslate: CustomTranslateService,
    private router: Router,
    private titleService: Title,
    private messagingService: MessagingService,
    private recordService: RecordService,
    private readonly _splash: SplashScreenService,
  ) {
    // Adds supported languages of the app
    translate.addLangs(AVAILABLE_LANGUAGE_CODES);

    // Check first if the callback passed has invalid format
    this.reconstructRedirectUrl(window.location.href);
  }

  ngOnInit() {
    this._fetchUserDetails();

    // Handle authentication redirect if it exists
    this.auth.authorize();
    this.chargebeeService.initSubscriptionInfoWhenAdmin();
    this._splash.dismiss();

    // Updates the page title (of main modules) based on route changes
    this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe((event : NavigationEnd) => {
          if (event.url !== undefined) {
            // URL Without the query param
            const url = this.router.serializeUrl(this.router.createUrlTree([]));
            return this.titleService.setTitle(this.translatePageTitle(this.getTitlePage(url)))
          }
        }
      );

    this.messagingService.initializePushNotification();

    this.translate.onLangChange.subscribe(() => {
      const url = this.router.serializeUrl(this.router.createUrlTree([]));
      //Title for AI Prompt Management to preserved on language change
      if (typeof url === 'string' && url.indexOf('ai-prompt-management') !== -1) {
        return;
      }
      this.titleService.setTitle(this.translatePageTitle(this.getTitlePage(url)))
    });
  }

  /**
   * Checks if the response redirect url from AUTH0 service has invalid format
   * Eg. http://localhost:4200/#access_token=blabla...
   *
   * @param responseRedirectUrl
   * @returns void
   */
  reconstructRedirectUrl(responseRedirectUrl) : void {

    // The response url (which invalidated the format)
    let currentRedirectUrl = environment.app_url + '/#access_token';

    // If the redirect url contains the invalid format,
    // get the correct angular redirect route and append the original data
    if (responseRedirectUrl.includes(currentRedirectUrl)) {

      let correctAngularRedirectRoute = '/account/callback?access_token';
      let reconstructedRedirectUrl = correctAngularRedirectRoute + responseRedirectUrl.slice(currentRedirectUrl.length);

      this.router.navigateByUrl(reconstructedRedirectUrl)
    }
  }

   /*
   * Translates the current page title based on the user's locale and add 'Fieldmagic -' at the start of each title.
   * If page title is not set, default it to 'Fieldmagic'
   *
   * @param strPageTitle
   * @returns string
   */
  translatePageTitle(strPageTitle): string {

    // FC-2549: Move the translatables from ngOnInit to this method
    // to initialize the it everytime this method is called
    this.ngOnInitTranslatables();
    if (filled(strPageTitle)) {
      return spf('%s | Fieldmagic', {
        args: [this.customTranslate.getTranslation(strPageTitle)],
      });
    }

    return this.customTranslate.getTranslation('Fieldmagic')
  }

  /**
   * Gets the current url of the page and strip it to return the current module name
   *
   * @param strUri
   * @returns string
   */
  getTitlePage(uri: OptionalValue<string>): OptionalValue<string> {
    if (filled(uri)) {
      return trim(uri, '/').replace(/\//g, '_');
    }

    return null;
  }

  /**
   * Initialize translatables
   */
  ngOnInitTranslatables(): void {
    this.customTranslate.initializeTransalateables([
      'admin',
      'leads',
      'customers',
      'sites',
      'contacts',
      'contracts',
      'jobs',
      'assets',
      'service_groups',
      'opportunities',
      'customer_invoices',
      'purchase_orders',
      'supplier_invoices',
      'dashboard',
      'config_asset',
      'config_inventory',
      'config_jobs',
      'config_contacts',
      'config_resources',
      'config_quote',
      'calendar',
      'schedule_recurring_jobs',
      'generate_recurring_jobs',
      'manage_assets',
      'recurring_invoices',
      'site_service_summary_report',
      'activities',
      'error',
      'account',
      'shared_location',
      'recurring_jobs',
      'recurring-invoices',
      'stock_levels',
      'support',
      'items',
      'timesheets',
      'credit_notes',
      'advance_reports',
      'document_library',
      'scheduler',
      'checklists',
      'warehouses',
      'subcontractors',
      'teams',
      'import',
      'pricebooks',
      'accounting_systems',
      'connect_datawarehouse',
      'api_keys',
      'payment_gateways',
      'omnichannel',
      'custom_dashboards',
      'customer_notification',
      'workflows',
      'studio',
      'system_config',
      'dropdowns',
      'pdf',
      'asset_types',
      'job_skills',
      'branding',
      'activity_log_types',
      'email_domains',
      'configure_customer_portal',
      'hide_or_show_modules',
      'quote_templates',
      'email_templates',
      'project_templates',
      'sms_templates',
      'job_templates',
      'job_safety_templates',
      'recycle_bin',
      'exports',
      'archived',
      'data_sharing_master',
      'data_sharing_children',
      'tax_codes',
      'account_codes',
      'payment_terms',
      'roles',
      'departments',
      'document_templates',
      'account_selection',
      'admin_users',
      'admin_departments',
      'admin_roles',
      'admin_teams',
      'admin_import',
      'admin_warehouses',
      'admin_pricebooks',
      'admin_integrations_accounting_systems',
      'admin_integrations_connect_datawarehouse',
      'admin_api_keys',
      'admin_point_of_sale_payment_gateways',
      'admin_omnichannel',
      'admin_custom_dashboards',
      'admin_customer_notification',
      'admin_workflows',
      'admin_studio',
      'admin_system_config',
      'admin_dropdowns',
      'admin_pdf',
      'admin_asset_types',
      'admin_job_skills',
      'admin_branding',
      'admin_activity_log_types',
      'admin_email_domains',
      'admin_html_template_builder',
      'admin_configure_displayed_modules',
      'admin_configure_displayed_modules_mobile',
      'admin_configure_customer_portal',
      'admin_configure_customer_portal_displayed_modules',
      'admin_configure_customer_portal_invoices',
      'admin_configure_customer_portal_assets',
      'admin_configure_customer_portal_quotes',
      'admin_configure_customer_portal_jobs',
      'admin_configure_customer_portal_sites',
      'admin_quote_templates',
      'admin_project_templates',
      'admin_document_templates',
      'admin_sms_templates',
      'admin_job_templates',
      'admin_job_safety_templates',
      'admin_recycle_bin',
      'admin_exports',
      'admin_archived',
      'admin_data_sharing_master',
      'admin_data_sharing_children',
      'admin_tax_codes',
      'admin_account_codes',
      'admin_payment_terms',
      'stock_levels_stocktake',
      'stock_levels_stocktake_history',
      'stock_levels_reorder',
      'account_register',
      'account_steps',
      'admin_artificial_intelligence',
      'admin_skills',
      'admin_status_config',
      'assets_map',
      'admin_qr_codes',
      'admin_resources',
      'admin_resource_types',
    ]);
  }

  private _fetchUserDetails() {
    /// don't bother if there's no active client
    if (blank(this.clientStoreService.getActiveClient())) {
      return;
    }

    /// use cached version of locale if present/available
    if (filled(this.localStorageService.getItem('user_locale'))) {
      this.translate.use(this.localStorageService.getItem('user_locale'));
    }

    this.recordService.getRecord('users', this.localStorageService.getItem('user_id'))
      .subscribe(data => {
        const details = get(data, 'record_details', {});

        this._setupLocale(get(details, 'locale', 'en'));
      });
  }

  private _setupLocale(locale: string = 'en') {
    locale = fallback(locale, {
      fallback: () => 'en',
    });


    if (! this.translate.getLangs().includes(locale)) {
      locale = 'en';
    }

    this.translate.use(locale);
    this.localStorageService.setItem('user_locale', locale);
  }
}
